import {Action, Button, ColorSchemeType, Separator} from 'platform/components';
import {Heading, HStack, Icon, Show, Spinner, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isEmptyArray, isNilOrEmpty} from 'ramda-adjunct';

import {useGetTagsForEntityQuery} from '@dms/api/core';
import {useGetSimilarVehiclesV2Query, useLazyGetSimilarVehiclesV2Query} from '@dms/api/sales';
import {
  EntityResourceIds,
  GetSimilarVehiclesApiResponse,
  SimilarVehicleResponseBody,
} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {handleApiError, VehicleWidget} from '@dms/shared';

import {
  buildArray,
  composePath,
  Nullish,
  RequiredTestIdProps,
  suffixTestId,
  useDebouncedValue,
} from 'shared/web';

const DEFAULT_VEHICLE_COUNT = 4;

type IdentifiedVehicleItemProps = RequiredTestIdProps & {
  vehicle: SimilarVehicleResponseBody;
  selectingVehicleId?: string | null;
  onVehicleSelect: (vehicleId: string) => void;
};

function IdentifiedVehicleItem(props: IdentifiedVehicleItemProps) {
  const {vehicle} = props;

  const {data: entityTags} = useGetTagsForEntityQuery({
    recordId: vehicle.id,
    resourceId: EntityResourceIds.vehicle,
  });

  const flags = entityTags?.map((tag) => {
    const [parsedColor, subtle] = tag.valueColor.split('.') as [
      ColorSchemeType,
      string | undefined,
    ];
    return {label: tag.valueName, colorScheme: parsedColor, isSubtle: !isNilOrEmpty(subtle)};
  });

  return (
    <VehicleWidget
      isBoxed
      flags={flags}
      vehicle={{
        id: vehicle.id,
        vin: vehicle.vin,
        registrationPlate: vehicle.registrationPlate,
        make: vehicle.make,
        modelFamily: vehicle.modelFamily,
        trim: vehicle.trim,
        variant: vehicle.variant,
        vehicleType: vehicle.type?.toString(),
        firstRegistration: vehicle.firstRegistration,
        mileage: vehicle.mileage,
        fuelType: vehicle.fuelType,
        transmission: vehicle.transmission,
        bodyStyle: vehicle.bodyStyle,
        driveType: vehicle.drive,
        photos: vehicle.photos,
        actions: buildArray<Action>()
          .add({
            type: 'button',
            title: i18n.t('entity.vehicle.labels.detail'),
            variant: 'outlined',
            leftIcon: 'action/launch',
            onClick: () => {
              window.open(composePath(vehiclesRoutes.detail, {params: {id: vehicle.id}}), '_blank');
            },
          })
          .add({
            type: 'button',
            title: i18n.t('general.actions.select'),
            variant: 'outlined',
            isLoading: props.selectingVehicleId === vehicle.id,
            isDisabled:
              isNotNil(props.selectingVehicleId) && props.selectingVehicleId !== vehicle.id,
            onClick: () => props.onVehicleSelect(vehicle.id),
          }),
      }}
      data-testid={suffixTestId('vehicleWidget', props)}
    />
  );
}

interface IdentifiedVehiclesProps extends RequiredTestIdProps {
  vin: string | Nullish;
  registrationPlate: string | Nullish;
  onVehicleSelect: (vehicleId: string) => void;
  selectingVehicleId?: string | null;
}

export function IdentifiedVehicles(props: IdentifiedVehiclesProps) {
  const [loadedMoreData, setLoadedMoreData] = useState<
    | {
        searchKey: string;
        vehicles: GetSimilarVehiclesApiResponse | undefined;
      }
    | undefined
  >(undefined);

  const debouncedVin = useDebouncedValue(props.vin, 700);
  const debouncedRegistrationPlate = useDebouncedValue(props.registrationPlate, 700);

  const searchKey = `${debouncedVin ?? ''}-${debouncedRegistrationPlate ?? ''}`;
  const moreVehicles =
    loadedMoreData?.searchKey === searchKey ? loadedMoreData.vehicles : undefined;

  const [getSimilarVehicles, {isLoading: isLoadingMore, isFetching: isFetchingMore}] =
    useLazyGetSimilarVehiclesV2Query();

  const {
    data: similarVehicles,
    isLoading,
    isFetching,
  } = useGetSimilarVehiclesV2Query(
    {
      size: DEFAULT_VEHICLE_COUNT,
      registrationPlateContains: debouncedRegistrationPlate ?? undefined,
      vinContains: debouncedVin ?? undefined,
    },
    {skip: !debouncedVin && !debouncedRegistrationPlate}
  );

  const onLoadMoreClick = () => {
    getSimilarVehicles({
      offset: DEFAULT_VEHICLE_COUNT,
      registrationPlateContains: debouncedRegistrationPlate,
      vinContains: debouncedVin,
    })
      .unwrap()
      .then((response) => {
        setLoadedMoreData({searchKey, vehicles: response.vehicles});
      })
      .catch(handleApiError);
  };

  const isLoadingVehicles = isFetching || isLoading;
  const isEmpty = isEmptyArray(similarVehicles?.vehicles);
  const isShowingResults = debouncedVin || debouncedRegistrationPlate;
  const allVehicles = [...(similarVehicles?.vehicles ?? []), ...(moreVehicles ?? [])];

  if (!isShowingResults) {
    return null;
  }

  return (
    <VStack spacing={4}>
      <Show when={isLoadingVehicles}>
        <HStack spacing={2} align="center" justify="center" height={22}>
          <Spinner size="small" />
          <Text size="small" color="tertiary" data-testid={suffixTestId('searching', props)}>
            {i18n.t('entity.vehicle.labels.searching')}
          </Text>
        </HStack>
      </Show>

      <Show when={isEmpty && !isLoadingVehicles}>
        <HStack spacing={2} align="center" justify="center" height={22}>
          <Icon value="notification/do_disturb" color="text.tertiary" size={4} />
          <Text size="small" color="tertiary" data-testid={suffixTestId('noVehicles', props)}>
            {i18n.t('entity.vehicle.labels.noMatchingVehicles')}
          </Text>
        </HStack>
      </Show>

      <Show when={!isLoadingVehicles && !isEmpty}>
        <Heading size={4}>{i18n.t('entity.workshopInterest.labels.identifiedVehicle')}</Heading>
        <VStack spacing={3}>
          {allVehicles.map((vehicle, index) => (
            <VStack key={vehicle.id}>
              <Show when={index > 0}>
                <Separator />
              </Show>
              <IdentifiedVehicleItem
                vehicle={vehicle}
                selectingVehicleId={props.selectingVehicleId}
                onVehicleSelect={props.onVehicleSelect}
                data-testid={suffixTestId(`vehicle-[${index}]`, props)}
              />
            </VStack>
          ))}
        </VStack>
      </Show>

      <Show
        when={
          (similarVehicles?.totalCount ?? 0) > DEFAULT_VEHICLE_COUNT &&
          isNil(moreVehicles) &&
          !isLoadingVehicles
        }
      >
        <HStack justify="center">
          <Button
            variant="link"
            onClick={onLoadMoreClick}
            isLoading={isLoadingMore || isFetchingMore}
            leftIcon="navigation/expand_more"
            title={`${i18n.t('entity.customer.actions.more')} (${(similarVehicles?.totalCount ?? 0) - DEFAULT_VEHICLE_COUNT})`}
            data-testid={suffixTestId('more', props)}
          />
        </HStack>
      </Show>
    </VStack>
  );
}
